/**
 * Acceso tipado a la configuracion publica del cliente.
 * SOLO variables VITE_*: acaban en el bundle y son publicas por diseño.
 * Ningun secreto pasa por aqui (docs/SECURITY_PRIVACY.md §4).
 *
 * La validacion de forma no es cosmetica. Una clave truncada o pegada a medias
 * supera cualquier comprobacion de "no vacio" y el fallo no aparece hasta el
 * canje de OAuth, como un 401 sin contexto. Aqui se detecta al arrancar y se
 * explica en pantalla.
 *
 * Este modulo es puro a proposito: no importa nada, no escribe logs y no toca
 * la red. `validateEnv` se puede probar sin montar la aplicacion.
 */
export interface AppEnv {
  supabaseUrl: string;
  supabaseAnonKey: string;
  oauthRedirectScheme: string;
  appEnv: 'local' | 'staging' | 'production';
  /**
   * Problemas de configuracion detectados, en texto mostrable al usuario.
   * NUNCA contienen el valor de la clave; como mucho su longitud.
   */
  problems: readonly string[];
  /** true si falta configuracion o no es valida: la app arranca igual, en modo solo local. */
  isIncomplete: boolean;
}

/** Valores en crudo, tal y como llegan de `import.meta.env`. */
export interface RawEnv {
  supabaseUrl: string;
  supabaseAnonKey: string;
  oauthRedirectScheme: string;
  appEnv: string;
}

/** URL de ejemplo de `.env.example`: copiar el fichero sin editarlo es un error real. */
const URL_DE_EJEMPLO = /^https:\/\/x{4,}\.supabase\.co\/?$/i;

/** Clave moderna de Supabase. La longitud minima descarta pegados parciales. */
const CLAVE_PUBLICABLE = /^sb_publishable_[A-Za-z0-9_-]{16,}$/;

/** Clave legada: un JWT de tres segmentos. */
const CLAVE_JWT = /^eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/;

const HOSTS_LOCALES = new Set(['localhost', '127.0.0.1', '[::1]']);

function problemaUrl(value: string): string | null {
  if (value === '') {
    return 'VITE_SUPABASE_URL esta vacia.';
  }
  if (URL_DE_EJEMPLO.test(value)) {
    return 'VITE_SUPABASE_URL sigue teniendo el valor de ejemplo de .env.example.';
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return 'VITE_SUPABASE_URL no es una URL absoluta valida.';
  }

  const esLocal = HOSTS_LOCALES.has(parsed.hostname);
  if (parsed.protocol === 'https:') return null;
  if (parsed.protocol === 'http:' && esLocal) return null;
  return 'VITE_SUPABASE_URL debe usar https (http solo se admite contra localhost).';
}

/**
 * Rol declarado por un JWT de Supabase, o null si no se puede leer.
 * Solo se usa para impedir que una clave de servidor acabe en el bundle.
 */
function rolDelJwt(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (payload === undefined) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const relleno = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json: unknown = JSON.parse(atob(relleno));
    if (typeof json !== 'object' || json === null) return null;
    const rol = (json as { role?: unknown }).role;
    return typeof rol === 'string' ? rol : null;
  } catch {
    return null;
  }
}

function problemaClave(value: string): string | null {
  if (value === '') {
    return 'VITE_SUPABASE_ANON_KEY esta vacia.';
  }
  // Barrera de seguridad: una clave de servidor en el cliente da acceso total
  // a la base de datos a cualquiera que abra el APK (docs/SECURITY_PRIVACY.md §1).
  if (value.startsWith('sb_secret_')) {
    return 'VITE_SUPABASE_ANON_KEY contiene una clave secreta (sb_secret_...). Esa clave nunca debe salir del servidor.';
  }
  if (CLAVE_PUBLICABLE.test(value)) {
    return null;
  }
  if (CLAVE_JWT.test(value)) {
    return rolDelJwt(value) === 'service_role'
      ? 'VITE_SUPABASE_ANON_KEY contiene la clave service_role. Esa clave nunca debe salir del servidor.'
      : null;
  }
  return `VITE_SUPABASE_ANON_KEY no tiene forma de clave de Supabase: se esperaba "sb_publishable_..." o un JWT de tres segmentos, y se han recibido ${String(value.length)} caracteres. Suele ser un pegado incompleto.`;
}

/** Validacion pura, sin dependencias del entorno. Es lo que se prueba. */
export function validateEnv(raw: RawEnv): AppEnv {
  const problems: string[] = [];

  const url = problemaUrl(raw.supabaseUrl);
  if (url !== null) problems.push(url);

  const clave = problemaClave(raw.supabaseAnonKey);
  if (clave !== null) problems.push(clave);

  const appEnv: AppEnv['appEnv'] =
    raw.appEnv === 'production' || raw.appEnv === 'staging' ? raw.appEnv : 'local';

  return {
    supabaseUrl: raw.supabaseUrl,
    supabaseAnonKey: raw.supabaseAnonKey,
    oauthRedirectScheme: raw.oauthRedirectScheme === '' ? 'com.cadencia.app' : raw.oauthRedirectScheme,
    appEnv,
    problems,
    isIncomplete: problems.length > 0,
  };
}

function read(key: string): string {
  const value = import.meta.env[key];
  return typeof value === 'string' ? value.trim() : '';
}

export function loadEnv(): AppEnv {
  return validateEnv({
    supabaseUrl: read('VITE_SUPABASE_URL'),
    supabaseAnonKey: read('VITE_SUPABASE_ANON_KEY'),
    oauthRedirectScheme: read('VITE_OAUTH_REDIRECT_SCHEME'),
    appEnv: read('VITE_APP_ENV'),
  });
}

export const env: AppEnv = loadEnv();
